import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, theme, App } from 'antd';
import { useCustomerDetail, useCustomerMutations, useCustomerLocations, useCustomerContacts, useCustomerLocationMutations, useCustomerOptions, useCustomerLocationDetail } from '@/hooks/useCustomers';
import { useCustomerPayment, useCustomerPaymentMutations } from '@/hooks/useCustomerPayment';
import { useCustomerTransactions } from '@/hooks/useTransactions';
import { useLocationPreload } from '@/hooks/useLocations';
import type { CustomerContactResponse, CustomerLocationResponse, HoldStatus } from '@/types/api/customer';
import { holdStatusToNumber } from '@/types/api/customer';
import LocationFormModal, { type LocationFormData } from '../LocationFormModal';
import { getCategories } from '@/apis/endpoints/categories';
import type { CategoryListItem } from '@/types/api/category';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { useCascadingLocations } from '@/hooks/useLocations';
import { useCustomerPermissions } from '@/hooks/useCustomerPermissions';
import EditCustomerHeaderModal from '../EditCustomerHeaderModal';
import EditBasicInfoModal from '../EditBasicInfoModal';
import EditFinancialInfoModal from '../EditFinancialInfoModal';
import EditPreferenceModal from '../EditPreferenceModal';
import { sanitizeLocationIds } from '@/utils/locationUtils';
import { parseEntityRouteId } from '@/utils/routeIdUtils';
import CustomerHeader from './CustomerHeader';
import CustomerTabs from './CustomerTabs';
import PrimaryInfoSection from './sections/PrimaryInfoSection';
import PaymentSection from './sections/PaymentSection';
import TransactionsSection, {
  type TransactionFilterState,
} from './sections/TransactionsSection';
import PreferenceSection from './sections/PreferenceSection';
import PageContainer from '@/components/common/PageContainer';

// Route-derived section selection (`?section=`), mirroring the SO / PO / Claim
// detail pages so the active section survives reload and is deep-linkable.
const SECTION_KEYS = ['primary', 'financial', 'transaction', 'preference'] as const;
type CustomerSection = (typeof SECTION_KEYS)[number];

const CustomerDetailPage = () => {
  const { t, i18n } = useTranslation('customers');
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { message: messageApi } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection: CustomerSection = SECTION_KEYS.includes(sectionParam as CustomerSection)
    ? (sectionParam as CustomerSection)
    : 'primary';
  const setActiveSection = (section: string) => {
    setSearchParams(section === 'primary' ? {} : { section });
  };
  // #91 — no contact modal state: the customer detail contact section is
  // view-only; contact create/edit lives in the Contacts module.
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null);
  const [editingLocation, setEditingLocation] = useState<LocationFormData | null>(null);
  const [locationReadOnly, setLocationReadOnly] = useState(false);
  const [includeInactiveContacts, setIncludeInactiveContacts] = useState(false);
  const [includeInactiveLocations, setIncludeInactiveLocations] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [togglingLocation, setTogglingLocation] = useState(false);
  const [settingDefaultShipping, setSettingDefaultShipping] = useState(false);
  const [settingDefaultBilling, setSettingDefaultBilling] = useState(false);

  // Edit modal states
  const [editBasicInfoModalOpen, setEditBasicInfoModalOpen] = useState(false);
  const [editFinancialInfoModalOpen, setEditFinancialInfoModalOpen] = useState(false);
  const [editPreferenceModalOpen, setEditPreferenceModalOpen] = useState(false);
  const [editHeaderModalOpen, setEditHeaderModalOpen] = useState(false);

  // Confirmation dialogs
  const toggleStatusDialog = useConfirmationDialog();
  const toggleLocationStatusDialog = useConfirmationDialog<CustomerLocationResponse>();
  const setDefaultShippingDialog = useConfirmationDialog<CustomerLocationResponse>();
  const setDefaultBillingDialog = useConfirmationDialog<CustomerLocationResponse>();

  // Convert customerId to number — tolerant of prefixed string list ids
  // (same D-VEND-NEW-1 class as the vendor routes)
  const numericCustomerId = parseEntityRouteId(customerId);

  // Fetch customer details
  const { customer, isLoading: loading, refresh } = useCustomerDetail(numericCustomerId);

  // Customer permissions
  const { canEdit, canAdd, getItemPermissions } = useCustomerPermissions(customer);

  // Fetch payment data
  const { paymentData: payment, refresh: refreshPayment } = useCustomerPayment(numericCustomerId);

  // Payment mutations
  const { createPaymentData, updatePaymentData } = useCustomerPaymentMutations(numericCustomerId ?? 0);

  // Fetch transactions — filters drive the API query params (server-side
  // filtering, #88, mirroring the vendor #66/#87 contract), not client-side
  // slicing. Uses the CUSTOMER transactions route (the section previously
  // piggybacked on the vendor endpoint).
  const [transactionFilters, setTransactionFilters] = useState<TransactionFilterState>({});
  const { transactions, isLoading: transactionsLoading } = useCustomerTransactions(
    numericCustomerId,
    transactionFilters
  );

  // Fetch contacts (view-only — #91)
  const { contacts, isLoading: contactsLoading } = useCustomerContacts(numericCustomerId);

  // Fetch customer locations
  const { locations, isLoading: locationsLoading, refetch: refreshLocations } = useCustomerLocations(numericCustomerId);

  // Location mutations
  const locationMutations = useCustomerLocationMutations(numericCustomerId ?? 0);

  // #91: no contact-detail fetch — the contact modal was removed from this page.

  // Fetch location detail for edit modal
  const { location: locationDetail, isLoading: locationDetailLoading } = useCustomerLocationDetail(
    numericCustomerId,
    editingLocationId
  );

  // Transform locationDetail to LocationFormData for the edit modal
  const editingLocationFromApi = useMemo((): LocationFormData | null => {
    if (!locationDetail) return null;
    return {
      locationName: locationDetail.locationName,
      isDefaultShipping: locationDetail.isDefaultShipping || false,
      isDefaultBilling: locationDetail.isDefaultBilling || false,
      country: locationDetail.country ?? '',
      countryId: locationDetail.countryId ?? null,
      stateId: locationDetail.stateId ?? null,
      state: locationDetail.state ?? '',
      cityId: locationDetail.cityId ?? null,
      city: locationDetail.city ?? '',
      postcode: locationDetail.postcode ?? '',
      address: locationDetail.address ?? '',
      addressLang: locationDetail.addressLang ?? null,
      notes: locationDetail.notes ?? '',
    };
  }, [locationDetail]);

  // Categories state for header edit modal (local state like wizard pattern)
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Fetch categories from API (reusable function for refresh - same pattern as wizard)
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await getCategories({ isActive: 1, pageSize: -1 });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch customer options
  const { customers: customerOptions } = useCustomerOptions(numericCustomerId ?? undefined);

  // State for resolved country code
  const [customerCountryCode, setCustomerCountryCode] = useState<string | null>(null);

  // Get cascading location data
  const { countries, states, cities } = useCascadingLocations(
    customerCountryCode,
    customer?.stateId ?? null,
    customer?.addressLang
  );

  // Resolve country code when customer and countries load
  useEffect(() => {
    if (customer?.countryId && countries.length > 0) {
      const country = countries.find((c) => c.countryId === customer.countryId);
      if (country && country.countryCode !== customerCountryCode) {
        setCustomerCountryCode(country.countryCode);
      }
    }
  }, [customer?.countryId, countries, customerCountryCode]);

  // Preload location data
  useLocationPreload();

  // Customer mutations
  const { toggleStatus, updateCustomer: updateCustomerMutation } = useCustomerMutations();

  // Helper to resolve parent company name
  const parentCompanyName = useMemo(() => {
    if (customer?.parentCompany?.companyName) {
      return customer.parentCompany.companyName;
    }
    const parentId = (customer as unknown as { parentCompanyId?: number })?.parentCompanyId;
    if (parentId && customerOptions) {
      const parent = customerOptions.find(c => c.customerId === parentId);
      return parent?.customerName || null;
    }
    return null;
  }, [customer, customerOptions]);

  // #91: contact handlers (add/edit/view-modal/set-primary/toggle) were removed —
  // the section is view-only and rows navigate to the Contacts module.

  const handleOpenContactInModule = (contact: CustomerContactResponse) => {
    // The org-scoped contactId IS the master-data contactId (mock seed keeps the
    // contactId sets identical); entityId + type give the detail page its scope.
    navigate(`/auth/contacts/${contact.contactId}?entityId=${numericCustomerId}&type=2`);
  };

  // Location handlers
  const handleEditLocation = (location: CustomerLocationResponse) => {
    // Set the location ID to trigger useCustomerLocationDetail fetch
    // Modal opens immediately with loading state, data loads via hook
    setEditingLocationId(location.locationId);
    setLocationReadOnly(false);
    setLocationModalOpen(true);
  };

  const handleViewLocation = (location: CustomerLocationResponse) => {
    // Open location modal in read-only mode
    // Modal opens immediately with loading state, data loads via hook
    setEditingLocationId(location.locationId);
    setLocationReadOnly(true);
    setLocationModalOpen(true);
  };

  const handleSaveLocation = async (locationData: LocationFormData) => {
    if (!numericCustomerId) return;

    const dataForApi = {
      locationName: locationData.locationName,
      isDefaultShipping: locationData.isDefaultShipping || false,
      isDefaultBilling: locationData.isDefaultBilling || false,
      ...sanitizeLocationIds({ countryId: locationData.countryId, stateId: locationData.stateId, cityId: locationData.cityId }),
      postcode: locationData.postcode,
      address: locationData.address,
      addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en',
      notes: locationData.notes,
    };

    try {
      if (editingLocationId) {
        await locationMutations.updateLocation(editingLocationId, dataForApi);
        messageApi.success(t('customer.notifications.locationUpdated'));
      } else {
        await locationMutations.createLocation(dataForApi);
        messageApi.success(t('customer.notifications.locationAdded'));
      }
      setLocationModalOpen(false);
      setEditingLocationId(null);
      refreshLocations();
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('customer.notifications.locationSaveFailed');
      messageApi.error(errorMessage);
      console.error('Save location error:', error);
    }
  };

  const handleAddLocation = () => {
    setEditingLocationId(null);
    setEditingLocation(null);
    setLocationReadOnly(false);
    setLocationModalOpen(true);
  };

  // Edit handlers
  const handleSaveCustomerHeader = async (data: {
    customerName: string;
    alternativeName: string;
    nickname: string;
    type: 'company' | 'individual';
    categoryIds: number[];
  }) => {
    if (!numericCustomerId || !customer) return;

    // IMPORTANT: Must include all existing customer fields to preserve data when updating header
    await updateCustomerMutation(numericCustomerId, {
      name: data.customerName,
      alternativeName: data.alternativeName || undefined,
      nickname: data.nickname || undefined,
      localTaxId: customer.localTaxId,
      individual: data.type === 'individual',
      customerCode: customer.customerCode,
      // Address fields
      ...sanitizeLocationIds({ countryId: customer.countryId, stateId: customer.stateId, cityId: customer.cityId }),
      postcode: customer.postcode || undefined,
      address: customer.address || undefined,
      addressLang: customer.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
      // Category
      categoryIds: data.categoryIds,
      // Phone fields - preserve existing phone data
      phoneCountryCode: customer.phoneCountryCode || undefined,
      phoneNumber: customer.phoneNumber || undefined,
      phoneExtension: customer.phoneExtension || undefined,
      // Contact fields
      email: customer.email || undefined,
      web: customer.web || undefined,
      fax: customer.fax || undefined,
      // Relationship fields
      internalContactUserId: customer.internalContact?.userId ?? undefined,
      parentCompanyId: customer.parentCompany?.companyId ?? undefined,
      // Notes fields
      accountNotes: customer.accountNotes || undefined,
      deliveryNotes: customer.deliveryNotes || undefined,
      salesNotes: customer.salesNotes || undefined,
    });
    // Refresh to get updated data (cache-aware, won't show full-page loading spinner)
    refresh();
  };

  const handleSaveBasicInfo = async (data: {
    phoneCountryCode: string;
    phoneNumber: string;
    phoneExtension?: string;
    email: string;
    web?: string;
    fax?: string;
    stateId: number | null;
    stateName?: string;
    cityId: number | null;
    cityName?: string;
    postcode?: string;
    address?: string;
    internalContactUserId?: number | null;
    parentCompanyId?: number | null;
    accountNotes?: string;
    deliveryNotes?: string;
    salesNotes?: string;
  }) => {
    if (!numericCustomerId || !customer) return;

    await updateCustomerMutation(numericCustomerId, {
      name: customer.name,
      localTaxId: customer.localTaxId,
      individual: customer.individual,
      customerCode: customer.customerCode,
      ...sanitizeLocationIds({
        countryId: customer.countryId,
        stateId: data.stateId ?? customer.stateId,
        cityId: data.cityId ?? customer.cityId,
      }),
      postcode: data.postcode || undefined,
      address: data.address || undefined,
      categoryIds: customer.categories.map((c) => c.categoryId),
      phoneCountryCode: data.phoneCountryCode,
      phoneNumber: data.phoneNumber,
      phoneExtension: data.phoneExtension || undefined,
      email: data.email,
      web: data.web || undefined,
      fax: data.fax || undefined,
      internalContactUserId: data.internalContactUserId ?? undefined,
      parentCompanyId: data.parentCompanyId ?? undefined,
      accountNotes: data.accountNotes || undefined,
      deliveryNotes: data.deliveryNotes || undefined,
      salesNotes: data.salesNotes || undefined,
    });
    // Refresh to get updated data (cache-aware, won't show full-page loading spinner)
    refresh();
  };

  const handleSaveFinancialInfo = async (data: {
    depositPercentage?: number;
    cashDiscountPercentage?: number;
    basePaymentTermId?: number;
    dueInDays?: number;
    creditLimit?: number;
    holdStatus: 'auto' | 'on_hold' | 'off_hold';
    primaryCurrency: string;
    taxTypeId?: number; // Optional in form, but zod schema validates it's required
    taxRate?: number;
  }) => {
    if (!numericCustomerId || !customer) return;

    // taxTypeId is validated as required by zod schema in the modal
    // This check is a safety guard
    if (!data.taxTypeId) {
      throw new Error('Tax type is required');
    }

    const paymentRequest = {
      depositPercentage: data.depositPercentage,
      cashDiscountPercentage: data.cashDiscountPercentage,
      basePaymentTermId: data.basePaymentTermId,
      dueInDays: data.dueInDays,
      creditLimit: data.creditLimit,
      // Hold status - API expects integer (1=Auto, 2=On Hold, 3=Off Hold)
      holdStatus: holdStatusToNumber((data.holdStatus || 'auto') as HoldStatus),
      primaryCurrency: data.primaryCurrency,
      taxTypeId: data.taxTypeId,
    };

    // If payment record doesn't exist, create it; otherwise update it
    if (!payment) {
      await createPaymentData(paymentRequest);
    } else {
      await updatePaymentData(paymentRequest);
    }
    refreshPayment();
  };

  // Show loading spinner
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-96">
        <Spin size="large" />
      </div>
    );
  }

  // Customer not found
  if (!customer) {
    return (
      <PageContainer>
        <div className="tw-text-center tw-py-8">
          <p style={{ color: token.colorTextSecondary }}>{t('customer.messages.customerNotFound')}</p>
        </div>
      </PageContainer>
    );
  }

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'primary':
        return (
          <PrimaryInfoSection
            customer={customer}
            parentCompanyName={parentCompanyName}
            countries={countries}
            states={states}
            cities={cities}
            canEdit={canEdit}
            onEditBasicInfo={() => setEditBasicInfoModalOpen(true)}
            contacts={contacts}
            includeInactiveContacts={includeInactiveContacts}
            onIncludeInactiveContactsChange={setIncludeInactiveContacts}
            onOpenContact={handleOpenContactInModule}
            onAddInContactsModule={() => navigate('/auth/contacts')}
            contactsLoading={contactsLoading}
            locations={locations}
            includeInactiveLocations={includeInactiveLocations}
            onIncludeInactiveLocationsChange={setIncludeInactiveLocations}
            onAddLocation={handleAddLocation}
            onEditLocation={handleEditLocation}
            onViewLocation={handleViewLocation}
            onSetDefaultShipping={(location) => setDefaultShippingDialog.open(location)}
            onSetDefaultBilling={(location) => setDefaultBillingDialog.open(location)}
            onToggleLocationStatus={(location) => toggleLocationStatusDialog.open(location)}
            locationsLoading={locationsLoading}
            canAdd={canAdd}
            getItemPermissions={getItemPermissions}
          />
        );

      case 'financial':
        return (
          <PaymentSection
            customer={customer}
            paymentData={payment}
            canEdit={canEdit}
            onEditFinancialInfo={() => setEditFinancialInfoModalOpen(true)}
          />
        );

      case 'transaction':
        return (
          <TransactionsSection
            transactions={transactions}
            loading={transactionsLoading}
            filters={transactionFilters}
            onFiltersChange={setTransactionFilters}
          />
        );

      case 'preference':
        return (
          <PreferenceSection
            paymentData={payment}
            contacts={contacts}
            canEdit={canEdit}
            onEditPreference={() => setEditPreferenceModalOpen(true)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      {/* Production Enhanced Table Styling */}
      <style>
        {`
          .production-enhanced .ant-table-cell {
            font-variant-numeric: tabular-nums;
            font-feature-settings: 'tnum';
          }

          .production-enhanced .ant-table-tbody > tr > td {
            height: 40px;
            min-height: 40px;
          }

          .production-enhanced .ant-table-thead > tr > th {
            font-weight: ${token.fontWeightStrong};
          }

          /* Must target both tr AND td because Ant Design sets background on cells */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-tag {
            opacity: 0.5;
            filter: grayscale(100%);
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="border-radius: 50%"],
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="borderRadius"] {
            background-color: ${token.colorTextTertiary} !important;
            opacity: 0.5;
          }
        `}
      </style>

      <CustomerHeader
        customer={customer}
        canEdit={canEdit}
        onEditClick={() => setEditHeaderModalOpen(true)}
        onToggleStatus={() => toggleStatusDialog.open(customer)}
        toggling={toggling}
      />

      <CustomerTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      >
        {renderContent()}
      </CustomerTabs>

      {/* Location Modal */}
      <LocationFormModal
        visible={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setEditingLocationId(null);
          setEditingLocation(null);
        }}
        onSuccess={handleSaveLocation}
        location={editingLocationId ? editingLocationFromApi : editingLocation}
        readOnly={locationReadOnly}
        loading={editingLocationId ? locationDetailLoading : false}
      />

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          if (numericCustomerId && customer) {
            setToggling(true);
            try {
              const newStatus = !customer.isActive;
              await toggleStatus(numericCustomerId, newStatus);
              const action = customer.isActive ? t('customer.messages.customerDeactivated') : t('customer.messages.customerActivated');
              messageApi.success(action);
              refresh();
              toggleStatusDialog.close();
            } catch (error) {
              messageApi.error(t('customer.messages.statusUpdateFailed'));
              console.error('Error toggling customer status:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={customer.isActive ? t('customer.modals.deactivateCustomerTitle') : t('customer.modals.activateCustomerTitle')}
        message={customer.isActive ? t('customer.modals.deactivateCustomerConfirm') : t('customer.modals.activateCustomerConfirm')}
        type={customer.isActive ? 'danger' : 'info'}
        confirmText={customer.isActive ? t('customer.actions.deactivate') : t('customer.actions.activate')}
        loading={toggling}
      />

      <ConfirmationDialog
        open={toggleLocationStatusDialog.isOpen}
        onClose={toggleLocationStatusDialog.close}
        onConfirm={async () => {
          const locationData = toggleLocationStatusDialog.data;
          if (locationData && numericCustomerId) {
            if (locationData.isActive && locationData.isDefaultShipping) {
              messageApi.error(t('customer.messages.cannotDeactivateDefaultLocation'));
              toggleLocationStatusDialog.close();
              return;
            }

            setTogglingLocation(true);
            try {
              const newStatus = !locationData.isActive;
              await locationMutations.toggleStatus(locationData.locationId, newStatus);
              refreshLocations();
              messageApi.success(
                newStatus
                  ? t('customer.notifications.locationActivated')
                  : t('customer.notifications.locationDeactivated')
              );
              toggleLocationStatusDialog.close();
            } catch (error) {
              messageApi.error(t('customer.messages.locationStatusUpdateFailed'));
              console.error('Error toggling location status:', error);
            } finally {
              setTogglingLocation(false);
            }
          }
        }}
        title={toggleLocationStatusDialog.data?.isActive ? t('customer.modals.deactivateLocationTitle') : t('customer.modals.activateLocationTitle')}
        message={toggleLocationStatusDialog.data?.isActive ? t('customer.modals.deactivateLocationConfirm') : t('customer.modals.activateLocationConfirm')}
        type={toggleLocationStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={toggleLocationStatusDialog.data?.isActive ? t('customer.actions.deactivate') : t('customer.actions.activate')}
        loading={togglingLocation}
      />

      <ConfirmationDialog
        open={setDefaultShippingDialog.isOpen}
        onClose={setDefaultShippingDialog.close}
        onConfirm={async () => {
          const locationData = setDefaultShippingDialog.data;
          if (locationData && numericCustomerId) {
            setSettingDefaultShipping(true);
            try {
              await locationMutations.setDefaultShipping(locationData.locationId);
              refreshLocations();
              messageApi.success(t('customer.notifications.defaultShippingLocationSet'));
              setDefaultShippingDialog.close();
            } catch (error) {
              messageApi.error(t('customer.messages.locationSetDefaultFailed'));
              console.error('Error setting default shipping location:', error);
            } finally {
              setSettingDefaultShipping(false);
            }
          }
        }}
        title={t('customer.modals.setDefaultShippingLocationTitle')}
        message={t('customer.modals.setDefaultShippingLocationConfirm')}
        type="info"
        confirmText={t('customer.actions.setAsDefaultShipping')}
        loading={settingDefaultShipping}
      />

      <ConfirmationDialog
        open={setDefaultBillingDialog.isOpen}
        onClose={setDefaultBillingDialog.close}
        onConfirm={async () => {
          const locationData = setDefaultBillingDialog.data;
          if (locationData && numericCustomerId) {
            setSettingDefaultBilling(true);
            try {
              await locationMutations.setDefaultBilling(locationData.locationId);
              refreshLocations();
              messageApi.success(t('customer.notifications.defaultBillingLocationSet'));
              setDefaultBillingDialog.close();
            } catch (error) {
              messageApi.error(t('customer.messages.locationSetDefaultFailed'));
              console.error('Error setting default billing location:', error);
            } finally {
              setSettingDefaultBilling(false);
            }
          }
        }}
        title={t('customer.modals.setDefaultBillingLocationTitle')}
        message={t('customer.modals.setDefaultBillingLocationConfirm')}
        type="info"
        confirmText={t('customer.actions.setAsDefaultBilling')}
        loading={settingDefaultBilling}
      />

      {/* Edit Modals */}
      <EditCustomerHeaderModal
        open={editHeaderModalOpen}
        onCancel={() => setEditHeaderModalOpen(false)}
        onSave={handleSaveCustomerHeader}
        customer={customer}
        categories={categories || []}
        loadingCategories={categoriesLoading}
        onCategoryCreated={fetchCategories}
      />

      <EditBasicInfoModal
        open={editBasicInfoModalOpen}
        onCancel={() => setEditBasicInfoModalOpen(false)}
        onSave={handleSaveBasicInfo}
        customer={customer}
      />

      <EditFinancialInfoModal
        open={editFinancialInfoModalOpen}
        onCancel={() => setEditFinancialInfoModalOpen(false)}
        onSave={handleSaveFinancialInfo}
        paymentData={payment}
        countryId={customer.countryId}
        localTaxId={customer.localTaxId}
      />

      <EditPreferenceModal
        open={editPreferenceModalOpen}
        onCancel={() => setEditPreferenceModalOpen(false)}
        onSuccess={() => {
          setEditPreferenceModalOpen(false);
          refreshPayment();
          refresh();
        }}
        customer={customer}
        contacts={contacts || []}
        paymentData={payment}
      />
    </PageContainer>
  );
};

export default CustomerDetailPage;
